import React from 'react';
import { useNavigate } from 'react-router-dom';

const AnthologyVirtualPersonas = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigate('/blog')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">
            From Bullet-Point Bios to Rich Backstories: How "Anthology" Uses Human AI Feel (and Vote) More Like Real People
          </h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>January 2025</span>
            <span className="mx-2">•</span>
            <span>9 min read</span>
            <span className="mx-2">•</span>
            <span>Research Deep Dive</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">The Big Question</h2>
          <p>
            If you want an LLM to stand in for a real survey respondent, simply telling it "you're a 45-year-old Hispanic dad in Texas—go!" rarely works. Short bios or QA-style demographic prompts give models a cardboard persona. Can we do better by giving the model an actual life story? That's the bet behind <strong>"Anthology: Virtual Personas for Language Models via an Anthology of Backstories."</strong>
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">What the Authors Built</h2>
          
          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Backstory Generator</h3>
          <p>
            They ask a base LLM one open-ended question: "Tell me about yourself." Sampling at T = 1.0 produces vivid, multi-paragraph life narratives that implicitly weave in age, upbringing, money struggles, faith, politics—the works.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Demographic "Census" of Each Story</h3>
          <p>
            A second pass of the model answers Pew-style demographic questions about the backstory's author to turn that narrative into a probabilistic demographic profile.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Smart Matching</h3>
          <p>
            Using a bipartite-matching algorithm (Hungarian for max-weight; a greedy variant for speed), they pick a subset of backstories whose aggregated demographics mirror the target survey's human sample.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Survey Simulation</h3>
          <p>
            Those matched backstories are prefixed to the model before it answers each question from three real Pew American Trends Panel waves (food biotech, political typology, AI & human enhancement). Responses are compared to the actual human data.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Why It Beats the Old Tricks</h2>
          
          <div className="bg-gray-900 p-6 rounded-lg my-6">
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2">Conditioning Method</th>
                  <th className="text-left py-2">Avg. Wasserstein Distance ↓</th>
                  <th className="text-left py-2">Consistency (Cronbach α) ↑</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">QA prompt (demographic Q-A)</td>
                  <td className="py-2">0.24 – 0.37</td>
                  <td className="py-2">0.68 – 0.74</td>
                </tr>
                <tr>
                  <td className="py-2">Anthology + Greedy Match</td>
                  <td className="py-2">0.20 – 0.31</td>
                  <td className="py-2">0.70 – 0.76</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Across all three surveys, Anthology cuts the LLM-human gap by up to <strong>18%</strong> and boosts internal-consistency metrics by <strong>27%</strong> relative to prompt-engineering baselines.
          </p>

          <p>
            It also generalises: even on subgroups not seen in the training set (e.g., 65+ rural Protestants), the back-story personas stay closer to the real distribution than any baseline.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Why This Matters</h2>
          <ul>
            <li><strong>Richer signals.</strong> A 400-word life story bakes in latent traits—values, mood, dialect—hard to capture in a five-line bio.</li>
            <li><strong>Lower bias.</strong> Off-the-shelf LLMs skew young, liberal, and college-educated. Matching a set of backstories to demographic weights reins that in.</li>
            <li><strong>Survey-design super-power.</strong> Researchers can pilot-test wording, spot subgroup divergences, or stress-test analysis code without paying for thousands of human respondents first.</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Honest Watch-outs</h2>
          <ul>
            <li><strong>No fine-tuning yet.</strong> Anthology relies on conditioning; if the base model lacks knowledge about, say, Navajo voters, no amount of story-time will conjure that data.</li>
            <li><strong>Hallucinated lives.</strong> The backstories are synthetic. If a generated "persona" claims to be a 9/11 first-responder, that detail may sway answers to patriotism questions in a way that real first responders would not.</li>
            <li><strong>Compute & prompt length.</strong> Prepending multiple 1-2 KB backstories chews into context window and latency.</li>
            <li><strong>Ethics & misuse.</strong> Better simulations can also mean better micro-targeted persuasion. The authors flag this explicitly and call for oversight.</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Take-Home for Practitioners</h2>
          <ul>
            <li><strong>Skip the bullet-point bio.</strong> Give your model a short memoir instead.</li>
            <li><strong>Sample broadly, match smartly.</strong> Generate lots of stories, then algorithmically down-sample to hit your demographic quotas.</li>
            <li><strong>Validate like a PhD advisor would.</strong> Track Wasserstein distance and correlation structure, not just top-1 accuracy.</li>
          </ul>

          <p>
            The code and a starter pack of ~10k backstories are open-sourced at the authors' repo—worth cloning if you need synthetic survey respondents that act a bit more… human.
          </p>

          <p className="text-lg font-semibold text-white">
            At HumanAI, we're building on insights like these to create AI respondents that don't just answer questions—they bring authentic human experiences to every response.
          </p>

          <p className="mt-6 font-semibold">
            <a href="#request-access" className="text-pink-400 underline hover:text-pink-300">Request early access</a> to see how we're making synthetic personas feel genuinely human.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>

        </div>
      </div>
    </div>
  );
};

export default AnthologyVirtualPersonas; 